import { useCallback, useMemo, useState } from "react";

import { useAccessCan } from "@/modules/dashboard/settings/hooks/useAccessCan";
import { useApiKeysData, useRolesData } from "@/modules/dashboard/settings/hooks/useAccessData";
import { useEffectiveSummaries } from "@/modules/dashboard/settings/hooks/useEffectiveSummaries";
import type { ApiKeyEntity } from "@/modules/dashboard/settings/services/apiKeysService";
import type { AssignedRole, EffectiveSummary } from "@/modules/dashboard/settings/types/access.types";

import { ACCESS_FILTER_ALL, ACCESS_TABLE_PAGE_SIZE } from "../constants/access.constants";
import { resolveApiKeyStatus, type ApiKeyStatus } from "../utils/apiKeyStatus";
import { normalizeSearchTerm } from "../utils/catalogFilters";
import { clampPage, pageSlice } from "../utils/pageSlice";

/** The four buckets of the KPI strip. The strip IS the filter, so each key is also a filter value. */
export type ApiKeysBucket = typeof ACCESS_FILTER_ALL | "active" | "no_roles" | "not_active";

export interface ApiKeyRow {
  key: ApiKeyEntity;
  status: ApiKeyStatus;
  roles: AssignedRole[];
  /** The roles fetch failed for THIS key: its access is unknown, which is not the same as "none". */
  rolesFailed: boolean;
  summary: EffectiveSummary | undefined;
}

export type ApiKeysBucketCounts = Record<ApiKeysBucket, number>;

/** Which bucket a row belongs to. Exported for its test — see `matchesRoleBucket` for why. */
export function matchesApiKeyBucket(row: ApiKeyRow, bucket: ApiKeysBucket): boolean {
  if (bucket === "active") return row.status === "active";
  if (bucket === "not_active") return row.status !== "active";
  // A key whose roles could not be read is UNKNOWN, never a confirmed zero — so it is not in this bucket.
  // Counting it as "sin roles" would report an anomaly we never established, on a security screen.
  if (bucket === "no_roles") return !row.rolesFailed && row.roles.length === 0;
  return true;
}

/**
 * Name and **masked prefix** — the prefix is the only part of the secret that survives creation, and it is
 * what an operator has in front of them when tracing a call in a log.
 */
export function matchesApiKeySearch(key: ApiKeyEntity, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return key.name.toLowerCase().includes(needle) || key.key.toLowerCase().includes(needle);
}

/**
 * The API key catalog as the page consumes it: search, role filter, bucket, pagination, and each key's
 * resolved authorization.
 *
 * Two properties of the backend shape everything here. `GET /api-keys` accepts **only** `skip` and `limit` —
 * no search, no status, no organization — so every filter runs in the browser over the page the provider
 * loaded at the API's own ceiling. And the endpoint has no `include=roles`, so each key's roles arrive
 * through a per-key request; when one of those fails the key lands in `rolesUnavailable` and its access is
 * reported as unknown rather than as zero, which would read as an authoritative "this key can do nothing".
 */
export function useApiKeysCatalog() {
  const { canAll } = useAccessCan();
  // The catalog's own state matters: with `api_key:read` but not `roles:read` it 403s, and resolving
  // summaries against an empty catalog would render every key as an authoritative-looking 0/N.
  const {
    roles,
    isLoading: catalogLoading,
    hasNoAccess: catalogUnavailable,
    load: reloadRoles,
  } = useRolesData();
  const keysData = useApiKeysData();
  const { keys, total, rolesByKey, rolesUnavailable, isLoading, hasNoAccess, load } = keysData;

  const [query, setQueryState] = useState("");
  const [roleId, setRoleIdState] = useState<string>(ACCESS_FILTER_ALL);
  const [bucket, setBucketState] = useState<ApiKeysBucket>(ACCESS_FILTER_ALL);
  const [page, setPage] = useState(1);

  // Page resets live in the SETTER, never in an effect: an effect would first render page 4 of a 1-page
  // result — an empty table under a total that says otherwise.
  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setPage(1);
  }, []);
  const setRoleId = useCallback((next: string) => {
    setRoleIdState(next);
    setPage(1);
  }, []);
  const setBucket = useCallback((next: ApiKeysBucket) => {
    setBucketState(next);
    setPage(1);
  }, []);

  const summaryById = useEffectiveSummaries(rolesByKey, roles);

  const rows = useMemo<ApiKeyRow[]>(
    () =>
      keys.map((key) => ({
        key,
        status: resolveApiKeyStatus(key),
        roles: rolesByKey[key.id] ?? [],
        rolesFailed: rolesUnavailable.has(key.id),
        summary: summaryById[key.id],
      })),
    [keys, rolesByKey, rolesUnavailable, summaryById],
  );

  const searched = useMemo(() => {
    const term = normalizeSearchTerm(query);
    return rows.filter(
      (row) =>
        (roleId === ACCESS_FILTER_ALL || row.roles.some((role) => role.id === roleId)) &&
        matchesApiKeySearch(row.key, term),
    );
  }, [rows, query, roleId]);

  const counts = useMemo<ApiKeysBucketCounts>(
    () => ({
      all: searched.length,
      active: searched.filter((row) => matchesApiKeyBucket(row, "active")).length,
      no_roles: searched.filter((row) => matchesApiKeyBucket(row, "no_roles")).length,
      not_active: searched.filter((row) => matchesApiKeyBucket(row, "not_active")).length,
    }),
    [searched],
  );

  const filtered = useMemo(
    () =>
      bucket === ACCESS_FILTER_ALL ? searched : searched.filter((row) => matchesApiKeyBucket(row, bucket)),
    [searched, bucket],
  );

  const safePage = clampPage(page, filtered.length);
  const paged = useMemo(() => pageSlice(filtered, page), [filtered, page]);

  const hasActiveFilters =
    query.trim() !== "" || roleId !== ACCESS_FILTER_ALL || bucket !== ACCESS_FILTER_ALL;

  const clearFilters = useCallback(() => {
    setQueryState("");
    setRoleIdState(ACCESS_FILTER_ALL);
    setBucketState(ACCESS_FILTER_ALL);
    setPage(1);
  }, []);

  /**
   * The roles a key currently holds, for seeding the assign-roles drawer. Answered from the full map rather
   * than from `rows`, which is only the visible PAGE — the drawer must not depend on where the row happens to
   * sit.
   */
  const assignedRoleIds = useCallback(
    (keyId: string) => (rolesByKey[keyId] ?? []).map((role) => role.id),
    [rolesByKey],
  );

  return {
    rows: paged,
    /** Rows after filtering — the pagination denominator. */
    total: filtered.length,
    /** Keys actually loaded, and what the backend says it holds: the truncation notice compares the two. */
    loadedCount: keys.length,
    backendTotal: total,
    counts,
    isLoading: isLoading || catalogLoading,
    hasNoAccess,
    catalogUnavailable,
    rolesUnavailable,
    roles,
    // `load` re-issues the query stored by the provider (`limit: 200`), so a reload still brings the whole
    // catalog. No wrapper: it is already stable and already returns a promise.
    reload: load,
    reloadRoles,
    assignedRoleIds,
    page: safePage,
    pageSize: ACCESS_TABLE_PAGE_SIZE,
    setPage,
    query,
    setQuery,
    roleId,
    setRoleId,
    bucket,
    setBucket,
    hasActiveFilters,
    clearFilters,
    // Lifecycle actions, forwarded untouched.
    assignRole: keysData.assignRole,
    removeRole: keysData.removeRole,
    create: keysData.create,
    disable: keysData.disable,
    enable: keysData.enable,
    updateAllowedIps: keysData.updateAllowedIps,
    getCurrentIp: keysData.getCurrentIp,
    // `:write`, not `:update` — `scopes[]` carries RBAC atoms and the backend guard collapses `update` to
    // `write`, so checking the composite would always be false.
    canCreate: canAll("api_key:write"),
    canUpdate: canAll("api_key:write"),
    canManageRoles: canAll("api_key:write", "roles:write"),
  };
}
